import React from "react";
import { Box, Rating, Stack, Typography } from "@mui/material";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import { getMessage, useLocale } from "../i18n/locale";

export default function ProfileRating({ rating, reviewCount }) {
  const { locale } = useLocale();
  const numericRating = Number(rating);

  if (!Number.isFinite(numericRating) || numericRating <= 0) return null;

  const formattedRating = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(numericRating);
  const hasReviewCount = Number.isFinite(Number(reviewCount));
  const formattedReviewCount = hasReviewCount
    ? new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(Number(reviewCount))
    : null;
  const reviewLabel = hasReviewCount
    ? getMessage(locale, Number(reviewCount) === 1 ? "profileRatingReviewOne" : "profileRatingReviewOther")
        .replace("{count}", formattedReviewCount)
    : null;
  const accessibleLabel = getMessage(
    locale,
    hasReviewCount ? "profileRatingAccessible" : "profileRatingAccessibleNoCount"
  )
    .replace("{rating}", formattedRating)
    .replace("{count}", formattedReviewCount ?? "");
  const ratingTone =
    numericRating >= 4.5
      ? { light: "#5F8F70", dark: "#A8D5B5", surface: "rgba(168, 213, 181, 0.16)" }
      : numericRating >= 3.5
        ? { light: "#A87835", dark: "#E8C88A", surface: "rgba(232, 200, 138, 0.16)" }
        : { light: "#B66A70", dark: "#E7A7AA", surface: "rgba(231, 167, 170, 0.16)" };

  return (
    <Box
      aria-label={accessibleLabel}
      sx={{
        display: "inline-flex",
        alignSelf: "flex-start",
        alignItems: "stretch",
        overflow: "hidden",
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "background.paper",
      }}
    >
      <Box
        sx={{
          display: "grid",
          placeItems: "center",
          minWidth: 58,
          px: 1.5,
          bgcolor: ratingTone.surface,
          borderInlineEnd: 1,
          borderColor: "divider",
        }}
      >
        <Typography
          component="span"
          sx={{
            fontFamily: "h4.fontFamily",
            fontSize: "1.5rem",
            fontWeight: 400,
            lineHeight: 1,
            color: (theme) => ratingTone[theme.palette.mode],
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {formattedRating}
        </Typography>
      </Box>

      <Stack spacing={0.25} justifyContent="center" sx={{ px: 1.5, py: 1 }}>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontWeight: 700,
            lineHeight: 1.2,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}
        >
          {getMessage(locale, "profileRatingLabel")}
        </Typography>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Rating
            value={numericRating}
            precision={0.1}
            readOnly
            size="small"
            icon={<StarRoundedIcon fontSize="inherit" />}
            emptyIcon={<StarRoundedIcon fontSize="inherit" />}
            sx={{
              color: "primary.main",
              "& .MuiRating-iconEmpty": { color: "divider" },
            }}
          />
          {reviewLabel && (
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
              {reviewLabel}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
